import React, {
  FC,
  useMemo,
  useState,
  useEffect,
  useRef,
  CSSProperties,
} from "react";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import { SelectList, SelectListOption } from "@narvar/nth-block-select-list";
import TextLink from "../TextLink";
import { SwatchesListProps, SwatchOption } from "./types";
import Language from "../Language";
import { useStyles } from "./SwatchesListStyles";

const GAP = 8;
const MAX_ROWS = 2;

const SwatchesList: FC<SwatchesListProps> = ({
  type,
  label,
  value,
  options = [],
  onChange,
}) => {
  const { linksNoneDefault, linksNoneHover } = useTheme();

  const styles = useStyles();

  const [expanded, setExpanded] = useState<boolean>(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const [hasMoreToggle, setHasMoreToggle] = useState(false);

  const selectedOption: SwatchOption | undefined = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const selectListOptions = options.map((o) => ({
    value: o.value,
    ariaLabel: o.label?.toString() ?? "",
    label: type === "image" ? (o.imageUrl ?? "") : o.label,
    isDisabled: o.disabled ?? false,
    isGrayedOut: o.grayedOut ?? false,
  })) satisfies SelectListOption[];

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (!listRef.current) return;

      const selectListContainer = listRef.current.firstChild as HTMLElement;
      const firstItem = selectListContainer.firstChild as HTMLElement;

      const calcContainerHeight =
        firstItem.offsetHeight * MAX_ROWS + GAP * (MAX_ROWS - 1);

      if (selectListContainer.offsetHeight / calcContainerHeight > 1) {
        setHasMoreToggle(true);
      }

      setContainerHeight(
        calcContainerHeight > selectListContainer.offsetHeight
          ? selectListContainer.offsetHeight
          : calcContainerHeight,
      );
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [listRef]);

  const handleChange = (newValue: string | string[] | null) => {
    if (!onChange) return;
    if (Array.isArray(newValue)) return;
    onChange(newValue === value || !newValue ? "" : newValue);
  };

  const handleToggleSeeMore = () => {
    setExpanded((d) => !d);
  };

  const wrapperStyles: CSSProperties =
    type === "image"
      ? {
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "0.5rem",
        }
      : {
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
        };

  return (
    <div className={styles.wrapper}>
      {label && (
        <div className={styles.wrapper}>
          <label className={styles.label}>{label}</label>
          {selectedOption && selectedOption.label}
        </div>
      )}
      <div
        ref={listRef}
        style={{
          width: "100%",
          height: expanded ? "auto" : containerHeight,
          overflow: "hidden",
        }}>
        <SelectList
          options={selectListOptions}
          component={type === "image" ? type : "button"}
          wrapperStyles={wrapperStyles}
          value={selectedOption?.value}
          isRequired
          limit={1}
          onChange={handleChange}
        />
      </div>
      {hasMoreToggle && (
        <div className={styles.wrapper}>
          <TextLink
            onClick={handleToggleSeeMore}
            linkStyle={{
              sDefault: linksNoneDefault,
              sHover: linksNoneHover,
            }}>
            <Language
              langKey={
                expanded
                  ? "returnsSwatchesListSeeLess"
                  : "returnsSwatchesListSeeMore"
              }
            />
          </TextLink>
        </div>
      )}
    </div>
  );
};

export default withThemeProvider(SwatchesList, {
  themeTokens: ["linksNoneDefault", "linksNoneHover"],
});
